import React, { useState, useEffect, useRef } from "react";
import { cn } from "../../utils/cn";

interface CheckboxProps {
  label?: string;
  checked?: boolean;
  intermediate?: boolean; // New prop for indeterminate state
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  size?: "small" | "medium" | "large" | "extra-small";
  color?: string;
  animationDuration?: number; // in milliseconds
  className?: string;
  disabled?: boolean;
}

const FleetrifyCheckbox: React.FC<CheckboxProps> = ({
  label,
  checked: initialChecked,
  intermediate = false, // Default value is false
  onChange,
  size = "medium",
  color = "#dc2626", // Default red color
  animationDuration = 200,
  className = "",
  disabled = false,
}) => {
  const [isChecked, setIsChecked] = useState(initialChecked || false);
  const [isAnimating, setIsAnimating] = useState(false);
  const checkboxRef = useRef<HTMLDivElement>(null);

  // Update isChecked when initialChecked changes
  useEffect(() => {
    setIsChecked(initialChecked || false);
  }, [initialChecked]);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.style.setProperty(
        "--animation-duration",
        `${animationDuration}ms`
      );
    }
  }, [animationDuration]);

  // Set indeterminate property on the DOM element
  useEffect(() => {
    if (checkboxRef.current) {
      (checkboxRef.current as any).indeterminate = intermediate;
    }
  }, [intermediate]);

  const handleCheckboxChange = () => {
    if (disabled || isAnimating || !onChange) return;

    setIsAnimating(true);
    const newChecked = !isChecked;

    onChange(
      {
        target: { checked: newChecked },
      } as React.ChangeEvent<HTMLInputElement>,
      newChecked
    );

    setTimeout(() => setIsAnimating(false), animationDuration);
  };

  const getSizeClass = () => {
    switch (size) {
      case "extra-small":
        return "w-4 h-4";
      case "small":
        return "w-5 h-5";
      case "large":
        return "w-8 h-8";
      default:
        return "w-6 h-6";
    }
  };

  const iconSizeClass = () => {
    switch (size) {
      case "extra-small":
        return "text-[10px]";
      case "small":
        return "text-xs";
      case "large":
        return "text-lg";
      default:
        return "text-sm";
    }
  };

  return (
    <div className={`flex gap-[6px] items-center ${className}`}>
      <div
        ref={checkboxRef}
        className={cn(
          "relative rounded-md border border-gray-400 flex items-center justify-center transition-all duration-[var(--animation-duration)]",
          getSizeClass(),
          disabled
            ? "bg-gray-200 border-gray-300 cursor-not-allowed"
            : isChecked
            ? `bg-[#143887] border-[#143887] ${
                onChange ? "cursor-pointer" : "cursor-default"
              }`
            : `bg-white ${onChange ? "cursor-pointer" : "cursor-default"}`,
          isAnimating && "transform scale-95"
        )}
        onClick={!disabled ? handleCheckboxChange : undefined}
        style={
          {
            "--animation-duration": `${animationDuration}ms`,
          } as React.CSSProperties
        }
      >
        {isChecked && !intermediate && (
          <svg
            className={`text-white transition-opacity duration-[var(--animation-duration)] ${iconSizeClass()}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
        {intermediate && (
          <div
            className={`bg-[#143887] ${
              size === "small"
                ? "w-3 h-0.5"
                : size === "large"
                ? "w-4 h-1"
                : "w-3.5 h-0.5"
            } rounded-full`}
          ></div>
        )}
      </div>
      {label && (
        <label
          className={cn(
            "ml-2 text-gray-700 font-medium text-[13px]",
            disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer"
          )}
          onClick={!disabled ? handleCheckboxChange : undefined}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default FleetrifyCheckbox;
